import { IInventory } from "../../services/inventoryApi";
import { AuctionPackage, AuctionWin } from "../../services/liveAuctionsApi";

export type InventoryMap = Map<string, IInventory>;

export type BuyerGroup = {
  buyerId: string;
  username: string;
  wins: AuctionWin[];
  packages: AuctionPackage[];
};

export const toCents = (value?: number) => Math.round((value || 0) * 100);

export const packageSellerCovered = (pkg: AuctionPackage) =>
  Math.max(
    0,
    pkg.actualShippingCostCents -
      pkg.customerPaidShippingCents -
      pkg.platformShippingCreditCents,
  );

export const applyCurrentInventoryCost = (
  win: AuctionWin,
  inventoryMap: InventoryMap,
): AuctionWin => {
  const currentInventory = inventoryMap.get(win.inventoryItemId);
  if (!currentInventory || win.costSource === "OVERRIDE") return win;
  return {
    ...win,
    costPerUnitCents: toCents(currentInventory.costPerUnit),
    inventoryItemNameSnapshot:
      currentInventory.itemName || win.inventoryItemNameSnapshot,
    brandSnapshot: currentInventory.brand || win.brandSnapshot,
  };
};

export const summarizeWins = (wins: AuctionWin[], inventoryMap: InventoryMap) => {
  const currentWins = wins.map((win) =>
    applyCurrentInventoryCost(win, inventoryMap),
  );
  const paid = currentWins.filter((win) => win.status === "PAID");
  return {
    totalUnits: currentWins.reduce((sum, win) => sum + win.quantity, 0),
    paidUnits: paid.reduce((sum, win) => sum + win.quantity, 0),
    uniqueBuyers: new Set(currentWins.map((win) => win.normalizedUsername))
      .size,
    grossRevenueCents: paid.reduce(
      (sum, win) => sum + win.winningPriceCents * win.quantity,
      0,
    ),
    cogsCents: paid.reduce(
      (sum, win) => sum + win.costPerUnitCents * win.quantity,
      0,
    ),
    platformFeesCents: paid.reduce((sum, win) => sum + win.platformFeeCents, 0),
    pendingAmountCents: currentWins
      .filter((win) => win.status === "PENDING")
      .reduce((sum, win) => sum + win.winningPriceCents * win.quantity, 0),
  };
};

export const summarizeBuyerGroup = (
  wins: AuctionWin[],
  packages: AuctionPackage[],
  inventoryMap: InventoryMap,
) => {
  const winTotals = summarizeWins(wins, inventoryMap);
  const activePackages = packages.filter((pkg) => pkg.status !== "CANCELLED");
  const packagingCostCents = activePackages.reduce(
    (sum, pkg) => sum + pkg.packagingCostCents,
    0,
  );
  const shippingCostCents = activePackages.reduce(
    (sum, pkg) => sum + pkg.actualShippingCostCents,
    0,
  );
  const buyerPaidShippingCents = activePackages.reduce(
    (sum, pkg) =>
      sum + pkg.customerPaidShippingCents + pkg.platformShippingCreditCents,
    0,
  );
  const sellerCoveredShippingCents = activePackages.reduce(
    (sum, pkg) => sum + packageSellerCovered(pkg),
    0,
  );
  const netProfitCents =
    winTotals.grossRevenueCents +
    buyerPaidShippingCents -
    winTotals.cogsCents -
    winTotals.platformFeesCents -
    packagingCostCents -
    shippingCostCents;
  const applicableRevenueCents =
    winTotals.grossRevenueCents + buyerPaidShippingCents;
  return {
    ...winTotals,
    packagingCostCents,
    shippingCostCents,
    buyerPaidShippingCents,
    sellerCoveredShippingCents,
    netProfitCents,
    marginPercent: applicableRevenueCents
      ? (netProfitCents / applicableRevenueCents) * 100
      : 0,
  };
};

export const lineProfitBeforeBoxCosts = (win: AuctionWin) =>
  win.winningPriceCents * win.quantity -
  win.platformFeeCents -
  win.costPerUnitCents * win.quantity;

export const packageUnits = (pkg: AuctionPackage, wins: AuctionWin[]) =>
  wins
    .filter((win) => pkg.auctionWinIds.includes(win._id))
    .reduce((sum, win) => sum + win.quantity, 0);

export const groupWinsByBuyer = (
  wins: AuctionWin[],
  packages: AuctionPackage[],
  inventoryMap: InventoryMap,
): BuyerGroup[] => {
  const currentWins = wins.map((win) =>
    applyCurrentInventoryCost(win, inventoryMap),
  );
  const winsByBuyer = new Map<string, AuctionWin[]>();
  currentWins.forEach((win) => {
    winsByBuyer.set(win.auctionBuyerId, [
      ...(winsByBuyer.get(win.auctionBuyerId) || []),
      win,
    ]);
  });
  return Array.from(winsByBuyer.entries())
    .map(([buyerId, buyerWins]) => ({
      buyerId,
      username: buyerWins[0]?.usernameSnapshot || "Unknown buyer",
      wins: buyerWins,
      packages: packages.filter((pkg) => pkg.auctionBuyerId === buyerId),
    }))
    .sort((a, b) => a.username.localeCompare(b.username));
};

export const isDateWithinRange = (
  value: string | Date,
  startYmd: string,
  endYmd: string,
) => {
  const date = typeof value === "string" ? new Date(value) : value;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const ymd = `${y}-${m}-${d}`;
  return ymd >= startYmd && ymd <= endYmd;
};

